import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { Folder, Plus } from 'lucide-react';
import { useState } from 'react';

import { kinds } from '@/app/site/scope';
import { useSiteScope } from '@/app/site/useSiteScope';
import {
  CatalogCard,
  CatalogGrid,
  CatalogGroup,
  CatalogHead,
  CatalogSearch,
  CatalogTable,
  NewCard,
  ViewToggle,
} from '@/features/catalog/Catalog';
import catalog from '@/features/catalog/Catalog.module.scss';
import { useViewMode } from '@/features/catalog/useViewMode';
import { Cover } from '@/features/catalog/Cover';
import { isPageModel, routePattern } from '@/features/catalog/pageModels';
import { PageSketch } from '@/features/catalog/Sketch';
import { sketchOf, type SketchKind } from '@/features/catalog/sketchOf';
import { useBlockTypes } from '@/features/catalog/useBlockTypes';
import { PAGES_SLUG, useSchemaTypes } from '@/features/catalog/useSchemaTypes';
import { contentFieldsCreate, contentTypesCreate } from '@/shared/api/generated/content/content';
import type { RecordBody, TypeBody } from '@/shared/api/generated/model';
import { recordsList } from '@/shared/api/generated/records/records';
import { ApiError } from '@/shared/api/http';
import { pluralize } from '@/shared/lib/plural';
import { Badge } from '@/shared/ui/Badge';
import { Button } from '@/shared/ui/Button';
import { toast } from '@/shared/ui/Toast';

import { NewRecordDialog } from '../ContentPage/NewRecordDialog';

const SECTIONS = ['секция', 'секции', 'секций'] as const;
const PAGES = ['страница', 'страницы', 'страниц'] as const;

/**
 * «Страницы» — страницы сайта плитками: эскиз первых секций, адрес, статус.
 * Страница — запись модели «Страницы» (pages) с полем-секциями; клик —
 * редактор страницы. Модели, чьи записи тоже страницы (статьи, кейсы), —
 * папками над ними.
 */
export function PagesPage() {
  const { siteId, env } = useParams({ from: '/authed/sites/$siteId/$env/pages' });
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const scope = useSiteScope();
  const [view, setView] = useViewMode('pages');
  const [search, setSearch] = useState('');
  const [creating, setCreating] = useState(false);
  const [setup, setSetup] = useState(false);

  const types = useSchemaTypes(siteId, env);
  const blocks = useBlockTypes(siteId, env);
  const pagesType = (types.data ?? []).find((type) => type.slug === PAGES_SLUG);
  const folders = (types.data ?? []).filter(isPageModel);

  const pages = useQuery({
    queryKey: ['records', pagesType?.id, { pages: true }],
    enabled: pagesType !== undefined,
    queryFn: async () => {
      const response = await recordsList(pagesType?.id ?? '', {
        sort: 'title',
        order: 'asc',
        per_page: 100,
      });

      if (response.status !== 200) {
        throw new Error('Неожиданный ответ на запрос страниц');
      }

      return response.data.items ?? [];
    },
  });

  const kindsOf = (page: RecordBody): SketchKind[] => {
    const sections = (page.data['sections'] ?? []) as { type?: string }[];

    return sections.map((section) =>
      sketchOf(
        section.type ?? '',
        blocks.data?.find((item) => item.type === section.type)?.fields ?? [],
      ),
    );
  };

  const pathOf = (page: RecordBody) => (page.slug === 'home' ? '/' : `/${page.slug ?? page.id}`);
  const countOf = (page: RecordBody) => ((page.data['sections'] ?? []) as unknown[]).length;
  const status = (page: RecordBody) =>
    page.status === 'published' ? (
      <Badge size="sm" tone="success">
        {page.has_unpublished_changes ? 'Есть правки' : 'На сайте'}
      </Badge>
    ) : (
      <Badge size="sm" tone="warning">
        Черновик
      </Badge>
    );

  const needle = search.trim().toLowerCase();
  const shownFolders = folders.filter(
    (type) => needle === '' || `${type.name} ${type.route}`.toLowerCase().includes(needle),
  );
  const list = (pages.data ?? [])
    .filter(
      (page) => needle === '' || `${page.title} ${pathOf(page)}`.toLowerCase().includes(needle),
    )
    // Главная — первой, дальше по названию.
    .sort((a, b) => Number(b.slug === 'home') - Number(a.slug === 'home'));

  const open = (page: RecordBody) => {
    void navigate({
      to: '/sites/$siteId/$env/r/$recordId',
      params: { siteId, env, recordId: page.id },
    });
  };

  // Модели «Страницы» ещё нет — заводится одной кнопкой: название и секции.
  const createPagesModel = async () => {
    setSetup(true);

    try {
      const created = await contentTypesCreate(siteId, kinds[env], {
        name: 'Страницы',
        slug: PAGES_SLUG,
        kind: 'collection',
        seo: true,
        description: 'Страницы сайта из секций. Главная — с адресом home',
      });
      const typeId = (created.data as { id: string }).id;

      await contentFieldsCreate(typeId, {
        key: 'title',
        name: 'Название',
        type: 'string',
        required: true,
      });
      await contentFieldsCreate(typeId, { key: 'sections', name: 'Секции', type: 'blocks' });
      void queryClient.invalidateQueries({ queryKey: ['content-types', siteId] });
    } catch (cause) {
      toast.error(cause instanceof ApiError ? cause.detail : 'Не удалось завести страницы');
    } finally {
      setSetup(false);
    }
  };

  const canWrite = scope?.can('content.write') ?? false;

  // Папки — модели-страницы (статьи, кейсы); видны и у сайта без «Страниц».
  const foldersGroup = shownFolders.length > 0 && (
    <CatalogGroup title="Папки" count={shownFolders.length}>
      <CatalogGrid>
        {shownFolders.map((type) => (
          <CatalogCard
            key={type.id}
            link={{
              to: '/sites/$siteId/$env/pages/$model',
              params: { siteId, env, model: type.slug },
            }}
            preview={<FolderPreview type={type} />}
            icon={<Folder size={16} />}
            title={type.name}
            subtitle={routePattern(type.route)}
            meta={<span>{pluralize(type.records_count, PAGES)}</span>}
          />
        ))}
      </CatalogGrid>
    </CatalogGroup>
  );

  return (
    <div className={catalog['page']}>
      <CatalogHead
        title="Страницы"
        subtitle="Страницы сайта из секций-компонентов. Страницы записей — кейса, статьи — собираются по шаблону своей модели."
        actions={
          pagesType !== undefined && (
            <>
              <ViewToggle value={view} onChange={setView} />
              {canWrite && (
                <Button
                  iconLeft={<Plus size={15} />}
                  onClick={() => {
                    setCreating(true);
                  }}
                >
                  Новая страница
                </Button>
              )}
            </>
          )
        }
      />

      {types.isPending ? null : pagesType === undefined ? (
        <div className={catalog['group']}>
          {foldersGroup}
          <p className={catalog['empty']}>
            У сайта ещё нет страниц. Заведём модель «Страницы»: у страницы будут название, адрес и
            секции из компонентов.
          </p>
          {(scope?.can('site.shape') ?? false) && (
            <div>
              <Button loading={setup} onClick={() => void createPagesModel()}>
                Завести страницы
              </Button>
            </div>
          )}
        </div>
      ) : (
        <>
          <div className={catalog['bar']}>
            <CatalogSearch
              value={search}
              onChange={setSearch}
              placeholder="Поиск по названию или адресу"
            />
          </div>

          {foldersGroup}

          {shownFolders.length > 0 && <h2 className={catalog['group__title']}>Страницы</h2>}

          {view === 'grid' ? (
            <CatalogGrid>
              {list.map((page) => (
                <CatalogCard
                  key={page.id}
                  link={{
                    to: '/sites/$siteId/$env/r/$recordId',
                    params: { siteId, env, recordId: page.id },
                  }}
                  preview={<PageSketch kinds={kindsOf(page)} />}
                  title={page.title === '' ? 'Без названия' : page.title}
                  subtitle={pathOf(page)}
                  badge={status(page)}
                  meta={<span>{pluralize(countOf(page), SECTIONS)}</span>}
                />
              ))}
              {canWrite && needle === '' && (
                <NewCard
                  onClick={() => {
                    setCreating(true);
                  }}
                >
                  <Plus size={20} />
                  Новая страница
                </NewCard>
              )}
            </CatalogGrid>
          ) : (
            <CatalogTable head={['Название', 'Адрес', 'Секций', 'Статус']}>
              {list.map((page) => (
                <tr key={page.id}>
                  <td>
                    <Link
                      to="/sites/$siteId/$env/r/$recordId"
                      params={{ siteId, env, recordId: page.id }}
                    >
                      {page.title === '' ? 'Без названия' : page.title}
                    </Link>
                  </td>
                  <td className={catalog['mono']}>{pathOf(page)}</td>
                  <td className={catalog['num']}>{countOf(page)}</td>
                  <td>{status(page)}</td>
                </tr>
              ))}
            </CatalogTable>
          )}
        </>
      )}

      {pagesType !== undefined && (
        <NewRecordDialog
          open={creating}
          onOpenChange={setCreating}
          typeID={pagesType.id}
          onDone={open}
        />
      )}
    </div>
  );
}

/** Превью папки — стопка обложек трёх последних записей, сверху свежая. */
function FolderPreview({ type }: { type: TypeBody }) {
  const latest = useQuery({
    queryKey: ['records', type.id, { folderPreview: true }],
    queryFn: async () => {
      const response = await recordsList(type.id, {
        per_page: 3,
        sort: 'updated_at',
        order: 'desc',
      });

      return response.status === 200 ? (response.data.items ?? []) : [];
    },
  }).data;

  const sheets = [...(latest ?? [])].reverse();

  return (
    <div className={catalog['folder']} aria-hidden="true">
      {(sheets.length === 0 ? [undefined] : sheets).map((record, index) => {
        const cover =
          record === undefined || type.cover_field === ''
            ? undefined
            : record.data[type.cover_field];

        return (
          <div key={record?.id ?? index} className={catalog['folder__sheet']}>
            <Cover
              mediaId={typeof cover === 'string' && cover !== '' ? cover : undefined}
              kinds={['text']}
            />
          </div>
        );
      })}
    </div>
  );
}
